import { useState, useEffect } from 'react';
import { X, Phone, Award, Radio, AlertCircle, ClipboardCheck } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';
import { reportPhoneConversion, trackButtonClick, trackEvent } from '@/utils/analytics';

interface BannerMessage {
  id: string;
  type: 'service' | 'announcement' | 'achievement' | 'urgency';
  icon: typeof AlertCircle;
  title: string;
  description: string;
  ctaText: string;
  ctaAction: () => void;
  color: string;
  gradient: string;
  timeRange?: {
    start: string; // HH:mm format
    end: string; // HH:mm format
  };
  priority?: number; // Higher priority shows first
}

const bannerMessages: BannerMessage[] = [
  {
    id: 'sleeping-area-audibility',
    type: 'urgency',
    icon: AlertCircle,
    title: 'San Francisco R-2 Audibility',
    description: 'Sleeping-area sound checks and upgrade planning for existing fire alarm systems.',
    ctaText: 'Review Requirement',
    ctaAction: () => {
      trackButtonClick('Review Requirement', 'banner');
      trackEvent('banner_cta_click', 'conversion', 'sleeping_area_audibility');
      window.location.href = '/services/fire-alarm#sleeping-area-audibility';
    },
    color: 'from-[#fff4dc] via-[#f8ead0] to-[#f3dfbd]',
    gradient: 'from-[#7a1f17] to-[#c9782c]',
    priority: 5,
  },
  {
    id: 'inspection-service',
    type: 'service',
    icon: ClipboardCheck,
    title: 'Inspection & Service Support',
    description: 'Annual testing, deficiency correction, documentation, and monitoring checks.',
    ctaText: 'Schedule Inspection',
    ctaAction: () => {
      trackButtonClick('Schedule Inspection', 'banner');
      trackEvent('banner_cta_click', 'conversion', 'inspection_service');
      window.location.href = '/quotes/services/fire-alarm';
    },
    color: 'from-[#fff6e5] via-[#f7e7cc] to-[#ecd5b0]',
    gradient: 'from-[#9b2a19] to-[#d17f2a]',
    priority: 4,
  },
  {
    id: 'monitoring-modernization',
    type: 'announcement',
    icon: Radio,
    title: 'Monitoring Modernization',
    description: 'Review phone-line costs, signal testing, and cellular/IP monitoring options.',
    ctaText: 'Calculate Costs',
    ctaAction: () => {
      trackButtonClick('Calculate Costs', 'banner');
      trackEvent('banner_cta_click', 'conversion', 'monitoring_calculator');
      window.location.href = '/quotes/services/24-7-monitoring#landline-cost-calculator';
    },
    color: 'from-[#fff8e8] via-[#f1e2c4] to-[#e7cfaa]',
    gradient: 'from-[#8a3b17] to-[#d99a2b]',
    priority: 3,
  },
  {
    id: 'experience',
    type: 'achievement',
    icon: Award,
    title: 'Serving the Bay Area Since 1986',
    description: 'Fire alarm, CCTV, access control, ERRCS, burglar alarm, and monitoring work.',
    ctaText: 'View Services',
    ctaAction: () => {
      trackButtonClick('View Services', 'banner');
      window.location.href = '/services';
    },
    color: 'from-[#fff6e5] via-[#f2dfbf] to-[#e8d0a8]',
    gradient: 'from-[#7a1f17] to-[#d99a2b]',
    priority: 2,
  },
  {
    id: 'after-hours',
    type: 'service',
    icon: Phone,
    title: 'After-Hours Service Calls',
    description: 'For active alarms, trouble signals, monitoring problems, or access issues.',
    ctaText: 'Call Now',
    ctaAction: () => {
      trackButtonClick('Call Now', 'banner');
      trackEvent('banner_cta_click', 'conversion', 'phone_call');
      reportPhoneConversion();
    },
    color: 'from-[#fff1e8] via-[#f5dcc7] to-[#eac49f]',
    gradient: 'from-[#7a1f17] to-[#990000]',
    timeRange: {
      start: '17:00', // After 5 PM
      end: '09:00', // Before 9 AM (next day)
    },
    priority: 6,
  },
];

interface SmartBannerProps {
  position?: 'top' | 'below-hero';
  dismissible?: boolean;
  autoRotate?: boolean;
  rotationInterval?: number; // in seconds
  showOnPages?: string[]; // page paths where banner should show
}

export function SmartBanner({
  position = 'below-hero',
  dismissible = true,
  autoRotate = true,
  rotationInterval = 8,
  showOnPages = ['/'],
}: SmartBannerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Filter messages based on time range and sort by priority
  const getAvailableMessages = () => {
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    return bannerMessages
      .filter((msg) => {
        if (!msg.timeRange) return true;
        
        const { start, end } = msg.timeRange;
        // Handle overnight time ranges (e.g., 17:00 to 09:00)
        if (start > end) {
          return currentTime >= start || currentTime <= end;
        }
        return currentTime >= start && currentTime <= end;
      })
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  };

  const availableMessages = getAvailableMessages();

  // Ensure currentIndex is valid when availableMessages changes
  useEffect(() => {
    if (currentIndex >= availableMessages.length && availableMessages.length > 0) {
      setCurrentIndex(0);
    }
  }, [availableMessages.length, currentIndex]);

  // Check if banner should be shown on current page
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (showOnPages.length > 0 && !showOnPages.includes(currentPath)) {
      setIsVisible(false);
    }
  }, [showOnPages]);

  // Check if user has dismissed banner in this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem('banner-dismissed');
    if (dismissed === 'true' && dismissible) {
      setIsDismissed(true);
    }
  }, [dismissible]);

  // Auto-rotate messages
  useEffect(() => {
    if (!autoRotate || isDismissed || !isVisible || availableMessages.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % availableMessages.length);
    }, rotationInterval * 1000);

    return () => clearInterval(timer);
  }, [autoRotate, rotationInterval, isDismissed, isVisible, availableMessages.length]);

  const handleDismiss = () => {
    trackEvent('banner_dismissed', 'engagement', availableMessages[currentIndex]?.id || 'unknown');
    setIsDismissed(true);
    if (dismissible) {
      sessionStorage.setItem('banner-dismissed', 'true');
    }
  };

  const handleCTAClick = () => {
    const message = availableMessages[currentIndex];
    if (message) {
      message.ctaAction();
    }
  };

  const handleDotClick = (index: number) => {
    if (index === currentIndex) return;
    trackEvent('banner_navigation', 'engagement', `message_${index}`);
    setCurrentIndex(index);
  };

  // Track banner view
  useEffect(() => {
    if (isVisible && !isDismissed && availableMessages.length > 0) {
      trackEvent('banner_viewed', 'engagement', availableMessages[currentIndex]?.id || 'unknown');
    }
  }, [currentIndex, isVisible, isDismissed, availableMessages]);

  if (isDismissed || !isVisible || availableMessages.length === 0) return null;

  const currentMessage = availableMessages[currentIndex];
  if (!currentMessage) return null;

  const Icon = currentMessage.icon;
  const showDots = autoRotate && availableMessages.length > 1;

  return (
    <div
      className={cn(
        'relative overflow-hidden transition-all duration-500',
        position === 'top' ? 'sticky top-[123px] z-30' : 'relative',
        isDismissed ? 'opacity-0 h-0' : 'opacity-100'
      )}
      role="region"
      aria-label="Service notice"
    >
      <div className={cn(
        'relative bg-gradient-to-r',
        currentMessage.color,
        // Reserve vertical space for dot controls on small screens so they never overlap text.
        showDots ? 'pt-3 sm:pt-3 pb-6 sm:pb-3' : 'py-3'
      )}>
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.28)_0%,transparent_42%,rgba(255,255,255,0.16)_100%)]" />
        </div>

        {/* Pulsing dots indicator */}
        {showDots && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {availableMessages.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleDotClick(index)}
                aria-current={index === currentIndex ? 'true' : undefined}
                className={cn(
                  // IMPORTANT: `src/index.css` sets global button padding (and min sizes) with `!important` on mobile.
                  // These dots must remain tiny visually, so we force-reset padding and min sizes using Tailwind's `!` modifier.
                  'group flex items-center justify-center !p-0 !min-h-0 !min-w-0',
                  // Keep an accessible tap target while rendering only a small dot inside.
                  'h-11 w-11 bg-transparent rounded-full'
                )}
                aria-label={`Go to message ${index + 1}`}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'block rounded-full transition-all',
                    index === currentIndex
                      ? 'h-1.5 w-6 bg-[#990000]'
                      : 'h-1.5 w-1.5 bg-[#990000]/35 group-hover:bg-[#990000]/55'
                  )}
                />
              </button>
            ))}
          </div>
        )}

        <div className="relative mx-auto max-w-7xl px-2 sm:px-4 md:px-6 lg:px-8">
          <div 
            key={currentIndex}
            className="flex flex-row flex-wrap items-center justify-center gap-2 sm:gap-3 md:gap-4 animate-fade-in py-1 sm:py-0"
          >
            {/* Icon and Title */}
            <div className="flex items-center gap-1.5 text-[#4a1d16] sm:gap-2">
              <Icon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="text-xs font-semibold leading-snug sm:text-sm md:text-[15px] whitespace-nowrap">
                {currentMessage.title}
              </span>
            </div>

            {/* Description */}
            <div className="hidden items-center gap-2 text-[#5d342b] md:flex md:flex-wrap md:justify-center">
              <span className="px-2 text-center text-xs font-medium leading-snug sm:text-sm md:text-[15px]">
                {currentMessage.description}
              </span>
            </div>

            {/* CTA Button */}
            <Button
              size="sm"
              className="border border-[#990000]/20 bg-white/85 text-[#990000] hover:bg-white font-semibold shadow-none transition-colors whitespace-nowrap text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
              onClick={handleCTAClick}
            >
              {currentMessage.ctaText}
            </Button>

            {/* Dismiss Button */}
            {dismissible && (
              <button
                onClick={handleDismiss}
                className="text-[#5d342b]/70 hover:text-[#4a1d16] transition-colors p-1 rounded hover:bg-white/30"
                aria-label="Dismiss banner"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Progress bar for auto-rotation */}
        {autoRotate && availableMessages.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#990000]/10">
            <div
              className="h-full bg-[#990000]/35 transition-all duration-300"
              style={{
                width: '100%',
                animation: `progress ${rotationInterval}s linear infinite`,
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes progress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        @keyframes fade-in {
          0% { opacity: 0; transform: translateY(-10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}
